import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { cleanLocalStorage } from './contexts/AuthContext';
import pwaService from './services/pwaService';

// 앱 시작 시 로컬 스토리지 정리 (개발 환경에서만, Mock 사용자 모드가 아닐 때만)
if (process.env.NODE_ENV === 'development') {
  const enableMockUser = localStorage.getItem('enableMockUser') === 'true';
  if (!enableMockUser) {
  cleanLocalStorage();
  }
}

// 모달 접근성 문제 해결을 위한 MutationObserver 설정
// root 요소에 aria-hidden="true"가 적용되는 것을 방지
const setupAccessibilityFix = () => {
  const rootElement = document.getElementById('root');
  if (!rootElement) return;

  // aria-hidden 속성이 변경될 때 감지하는 MutationObserver
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (
        mutation.type === 'attributes' &&
        mutation.attributeName === 'aria-hidden' &&
        rootElement.getAttribute('aria-hidden') === 'true'
      ) {
        // aria-hidden="true"가 적용되었을 때 제거
        rootElement.removeAttribute('aria-hidden');
      }
    });
  });

  // 속성 변경 감지 시작
  observer.observe(rootElement, { attributes: true });
};

// DOM이 로드된 후 접근성 수정 설정
document.addEventListener('DOMContentLoaded', setupAccessibilityFix);

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// PWA Service Worker 등록 및 구독 상태 복원
import { isProduction } from './config/environment';
import pushNotificationService from './services/pushNotificationService';

// PWA 모드 감지 함수
const isPWAStandalone = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  // PWA standalone 모드 감지
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  
  // iOS Safari의 경우 추가 체크
  const isIOSStandalone = (window.navigator as any).standalone === true;
  
  return isStandalone || isIOSStandalone;
};

// PWA 진입 시 Service Worker 캐시만 정리 (localStorage는 보존)
const clearServiceWorkerCacheOnPWAEntry = async (): Promise<void> => {
  if (!isPWAStandalone()) {
    return; // PWA 모드가 아니면 스킵
  }

  try {
    console.log('📱 [PWA 진입] PWA standalone 모드 감지 - Service Worker 캐시 정리 시작');
    await pwaService.clearCaches();
    console.log('✅ [PWA 진입] Service Worker 캐시 정리 완료 (localStorage는 보존됨)');
  } catch (error) {
    console.error('❌ [PWA 진입] Service Worker 캐시 정리 실패:', error);
  }
};

if (isProduction()) {
  pwaService.registerServiceWorker()
    .then(async (success) => {
      if (success) {
        console.log('주담 클라우드 PWA가 성공적으로 등록되었습니다.');
        
        // PWA 모드 진입 시 Service Worker 캐시만 정리
        await clearServiceWorkerCacheOnPWAEntry();
        
        // Service Worker 등록 후 기존 구독 확인 및 복원 시도
        try {
          const token = localStorage.getItem('token');
          if (token) {
            console.log('🔄 [PWA 등록] 로그인 상태 확인 - 기존 푸시 구독 확인 중...');
            
            // Service Worker가 완전히 준비될 때까지 대기
            await new Promise(resolve => setTimeout(resolve, 1500));
            
            // 구독 상태 확인 및 복원
            await pushNotificationService.checkAndRestoreSubscription();
            console.log('✅ [PWA 등록] 푸시 구독 상태 확인 완료');
          }
        } catch (error) {
          console.warn('⚠️ [PWA 등록] 푸시 구독 확인 실패 (로그인 후 자동 복원됨):', error);
        }
        
        // 푸시 알림 권한 요청 (선택사항)
        // 사용자 제스처 없이 권한 요청 시 Violation 경고 발생하므로
        // 이미 권한이 있는 경우에만 확인하고, 없는 경우는 사용자가 명시적으로 요청할 때까지 대기
        if ('Notification' in window) {
          const currentPermission = Notification.permission;
          if (currentPermission === 'granted') {
            console.log('푸시 알림이 이미 활성화되어 있습니다.');
            console.log('🔔 [푸시 알림] 로그인 후 구독 초기화 예정');
          } else if (currentPermission === 'default') {
            // 권한이 아직 요청되지 않은 경우, 사용자 제스처 후에만 요청하도록 함
            // 여기서는 요청하지 않고, 사용자가 명시적으로 알림 설정 페이지에서 요청하도록 함
          }
        }
      }
    })
    .catch((error) => {
      console.error('PWA 등록 실패:', error);
    });
} else {
  console.log('개발 모드에서는 Service Worker가 등록되지 않습니다.');
  
  // 개발 모드에서도 PWA 모드 감지 및 캐시 정리 (테스트용)
  if (isPWAStandalone()) {
    console.log('📱 [개발 모드] PWA 모드 감지 - Service Worker 캐시 정리 (테스트)');
    clearServiceWorkerCacheOnPWAEntry();
  }
} 